import { useMemo, useEffect, useRef } from "react";
import { EditableSelectCell } from "../EditableSelectCell";
import { getFamilyTableOptions } from "./utils";
import { Checkbox } from "@mui/material";

// ✅ Proper React component — hooks are legal here
function FamilyTableCell({ row, cell, allTables, handleFieldUpdate }) {
  const Component = row.original.Components;
  const spec = row.original.Spec;
  const value = cell.getValue();

  const fullOptions = useMemo(() => {
    if (!Component) return [];
    return getFamilyTableOptions(allTables, Component, spec);
  }, [allTables, Component, spec]);

  const nameOptions = useMemo(() => {
    const seen = new Set();
    return (fullOptions || [])
      .map((cur) => String(cur?.Family_Name ?? "").trim())
      .filter((n) => {
        if (!n || n === "-") return false;
        if (seen.has(n)) return false;
        seen.add(n);
        return true;
      });
  }, [fullOptions]);

  const safeValue = nameOptions.includes(value) ? value : "";

  // ✅ Track last value we wrote so we don't fire handleFieldUpdate unnecessarily
  const lastAutoSelected = useRef(null);

  // Auto-select if only one option — but ONLY if the row doesn't already have it
  useEffect(() => {
    if (!Component) return;
    if (nameOptions.length !== 1) return;
    const onlyOption = nameOptions[0];
    // Skip if row already has this value or we already set it this session
    if (value === onlyOption) return;
    if (lastAutoSelected.current === onlyOption) return;
    lastAutoSelected.current = onlyOption;
    handleFieldUpdate(row, cell.column.id, onlyOption);
  }, [Component, nameOptions]); // intentionally omit value/row/cell to avoid loops

  // ✅ Track last FamilyTableTarget we wrote
  const lastTableTarget = useRef(null);

  // Sync FamilyTableTarget — but ONLY when it actually changes
  useEffect(() => {
    const tableTarget =
      fullOptions.find((cur) => cur.Family_Name === safeValue)?.Family_Table ||
      "";
    // Skip if the row already has this value or we already wrote it
    if (row.original.FamilyTableTarget === tableTarget) return;
    if (lastTableTarget.current === tableTarget) return;
    lastTableTarget.current = tableTarget;
    handleFieldUpdate(row, "FamilyTableTarget", tableTarget);
  }, [safeValue, fullOptions]); // intentionally omit row to avoid loops

  if (!Component) return <EditableSelectCell disabled />;

  return (
    <EditableSelectCell
      value={safeValue}
      options={nameOptions}
      onChange={(newVal) => handleFieldUpdate(row, cell.column.id, newVal)}
    />
  );
}

// ✅ columns is now a pure function — no hooks inside
export const columns = (
  allTables,
  handleFieldUpdate,
  handleDeleteRow,
  handleToggleRow,
) => [
  {
    id: "select",
    header: "Select",
    enableColumnActions: false,
    Cell: ({ row }) => (
      <Checkbox
        checked={!!row.original.select}
        onChange={(e) => handleToggleRow(row.original, e.target.checked)}
        size="small"
      />
    ),
    size: 20,
  },
  {
    accessorKey: "Spec",
    header: "Spec",
    enableColumnActions: false,
    size: 75,
    enableEditing: false,
  },
  {
    accessorKey: "Components",
    header: "Component",
    enableColumnActions: false,
    size: 140,
    enableEditing: false,
  },
  {
    accessorKey: "Size",
    header: "Size",
    enableColumnActions: false,
    size: 180,
    enableEditing: false,
  },
  {
    accessorKey: "Types",
    header: "Types",
    enableColumnActions: false,
    enableEditing: false,
    size: 90,
  },
  {
    accessorKey: "Material",
    header: "Material",
    enableColumnActions: false,
    enableEditing: false,
    size: 110,
  },
  {
    accessorKey: "Requirements",
    header: "Requirements",
    enableColumnActions: false,
    enableEditing: false,
    size: 140,
  },
  {
    accessorKey: "Material Description",
    header: "Material Description",
    enableColumnActions: false,
    enableEditing: false,
    size: 220,
  },
  {
    accessorKey: "MaterialCode",
    header: "Material Code",
    enableColumnActions: false,
    enableEditing: false,
    size: 120,
  },
  {
    accessorKey: "Family Table",
    header: "Family Table",
    enableColumnActions: false,
    size: 150,
    Cell: (props) => (
      <FamilyTableCell
        {...props}
        allTables={allTables}
        handleFieldUpdate={handleFieldUpdate}
      />
    ),
  },
];
